import { useMutation } from '@tanstack/react-query';
import { retrieveBankConfirmation, newUuid } from '../lib/api';
import { appConfig } from '../config';
import { CopyButton } from './CopyButton';
import { useToast } from './Toast';
import type { BankConfirmationSlipResult, ForceBankSettlementResponse } from '../lib/types';

interface Props {
  result: ForceBankSettlementResponse;
  sovereignOverride: boolean;
}

/**
 * Retrieves a real bank-issued confirmation / slip for the settled KoreID and, when the
 * bank returned a document, offers it for download. Never fabricates a slip.
 */
export function BankConfirmationButton({ result, sovereignOverride }: Props): JSX.Element {
  const toast = useToast();
  const mutation = useMutation<BankConfirmationSlipResult, Error>({
    mutationFn: () =>
      retrieveBankConfirmation({
        koreId: result.koreId,
        bankRequestReference: result.bankRequestReference,
        bankName: result.account.bankName,
        settlementReference: result.settlement.settlementReference ?? undefined,
        uetr: result.account.uetr ?? undefined,
        requestedBy: appConfig.allowedUser,
        correlationId: newUuid(),
        sovereignOverride,
      }),
    onError: (e) => toast.push('error', `Bank confirmation retrieval failed: ${e.message}`),
    onSuccess: (r) => toast.push(r.documentBase64 ? 'success' : 'info', `Bank confirmation status: ${r.status}`),
  });

  const download = (slip: BankConfirmationSlipResult): void => {
    if (!slip.documentBase64) return;
    const contentType = slip.documentContentType ?? 'application/octet-stream';
    const link = document.createElement('a');
    link.href = `data:${contentType};base64,${slip.documentBase64}`;
    const ext = contentType.includes('pdf') ? 'pdf' : 'bin';
    link.download = `bank-confirmation-${slip.koreId}.${ext}`;
    link.click();
  };

  const slip = mutation.data;

  return (
    <div className="rounded-lg border border-slate-700 bg-kore-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Bank-Issued Confirmation / Slip</h3>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rounded bg-kore-accent px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {mutation.isPending ? 'Retrieving…' : 'Retrieve Bank-Issued Confirmation / Slip'}
        </button>
      </div>

      {slip && (
        <dl className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Status</dt><dd className="font-medium text-white">{slip.status}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Bank Reference</dt><dd className="font-medium text-white break-all">{slip.bankReference ?? '—'}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Bank Searchable Reference</dt><dd className="font-medium text-white break-all">{slip.bankSearchableReference ?? '—'}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Settlement Reference</dt><dd className="font-medium text-white break-all">{slip.settlementReference ?? '—'}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Confirmation Timestamp UTC</dt><dd className="font-medium text-white">{slip.confirmationTimestampUtc ?? '—'}</dd></div>
          <div className="mt-2 flex flex-wrap gap-2">
            <CopyButton label="Bank Reference" value={slip.bankReference} />
            <CopyButton label="Bank Searchable Ref" value={slip.bankSearchableReference} />
            {slip.documentBase64 && (
              <button type="button" onClick={() => download(slip)} className="rounded border border-green-500 bg-green-800 px-2 py-1 text-xs text-white hover:opacity-90">
                Download slip
              </button>
            )}
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-slate-400">Raw bank-issued response</summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded bg-black/50 p-2 text-xs text-slate-300">
              {typeof slip.rawBankIssuedResponse === 'string' ? slip.rawBankIssuedResponse : JSON.stringify(slip.rawBankIssuedResponse, null, 2)}
            </pre>
          </details>
        </dl>
      )}
    </div>
  );
}
